import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Edit,
  Eye,
  Lock,
  Plus,
  Power,
  RefreshCw,
  Trash2,
  Unlock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  useChangeStateStatusMutation,
  useDeleteStateMutation,
} from '../../../apis/toolsApi';

import { AppModalRef } from '../../../model';
import BaseModal from '../../../ui-components/BaseModal';
import StatusTag from '../../../ui-components/StatusTag';
import {
  ActionMenu,
  type ActionMenuItem,
  IconButton,
  UniversalModuleView,
} from '../../../ui-components/listing';
import { API_CONSTANTS } from '../../../utils/constants';

import type { ModalData, State } from '../model';
import type { ColDef } from 'ag-grid-community';

import StateFormModal from './StateFormModal';
import StateDetailsModal from './components/StateDetailsModal';

export interface StatesPageProps {
  initialModal?: { mode: 'create' | 'edit'; stateId?: string };
  modalReturnTo?: string;
}

const States: React.FC<StatesPageProps> = ({ initialModal, modalReturnTo }) => {
  const navigate = useNavigate();

  const [changeStatus, { isLoading }] = useChangeStateStatusMutation();
  const [deleteState, { isLoading: isDeleteLoading }] =
    useDeleteStateMutation();

  const [refreshToken, setRefreshToken] = useState(0);
  const [isFormOpen, setIsFormOpen] = useState(Boolean(initialModal));
  const [formMode, setFormMode] = useState<'create' | 'edit'>(
    initialModal?.mode ?? 'create'
  );
  const [formId, setFormId] = useState<string | undefined>(
    initialModal?.stateId
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalData, setModalData] = useState<ModalData | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedState, setSelectedState] = useState<State | null>(null);

  const [labels, setLabels] = useState<AppModalRef>({
    title: 'Confirm Status Change',
    content: '',
    confirmLabel: 'Yes',
    cancelLabel: 'Cancel',
  });

  useEffect(() => {
    if (!initialModal) return;
    setIsFormOpen(true);
    setFormMode(initialModal.mode);
    setFormId(initialModal.stateId);
  }, [initialModal]);

  const openCreateState = useCallback(() => {
    setFormMode('create');
    setFormId(undefined);
    setIsFormOpen(true);
  }, []);

  const openEditState = useCallback((stateId?: number) => {
    if (!stateId) return;
    setFormMode('edit');
    setFormId(String(stateId));
    setIsFormOpen(true);
  }, []);

  const handleDelete = useCallback((stateId: number) => {
    setLabels({
      title: 'Delete State',
      content: 'Are you sure you want to delete this state?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });
    setModalData({ status: 'DELETE', selectedIds: [stateId] });
    setIsModalOpen(true);
  }, []);

  const handleViewDetails = useCallback((row: State) => {
    setSelectedState(row);
    setIsDetailsOpen(true);
  }, []);

  const handleStatusChange = useCallback(
    (selectedIds: number[], status: string) => {
      if (!selectedIds.length) {
        toast.error(`Please select at least one state`);
        return;
      }
      setLabels({
        title: 'Confirm Status Change',
        content: `Are you sure you want to ${status} this State(s)?`,
        confirmLabel: 'Yes',
        cancelLabel: 'Cancel',
      });
      setModalData({ status, selectedIds });
      setIsModalOpen(true);
    },
    []
  );

  const buildActions = useCallback(
    (row: State) => {
      const actions: ActionMenuItem[] = [];
      actions.push({
        label: 'View Details',
        icon: Eye,
        onClick: () => handleViewDetails(row),
      });
      actions.push({
        label: 'Edit',
        icon: Edit,
        onClick: () => openEditState(row.stateId),
      });
      actions.push({
        label: row.status === 'Active' ? 'Inactivate' : 'Activate',
        icon: Power,
        onClick: () =>
          handleStatusChange(
            [row.stateId],
            row.status === 'Active' ? 'Inactive' : 'Active'
          ),
      });
      actions.push({
        label: 'Delete',
        icon: Trash2,
        onClick: () => handleDelete(row.stateId),
        tone: 'danger',
      });
      return actions;
    },
    [openEditState, handleDelete, handleStatusChange, handleViewDetails]
  );

  const NameCellRenderer = useCallback(
    (params: { data?: State }) => {
      const row = params.data;
      if (!row) return '-';
      return (
        <button
          type="button"
          onClick={() => openEditState(row.stateId)}
          className="text-brand-600 hover:underline hover:text-brand-800 cursor-pointer transition font-semibold truncate"
        >
          {row.state || '-'}
        </button>
      );
    },
    [openEditState]
  );

  const ActionsCellRenderer = useCallback(
    (params: { data?: State }) => {
      if (!params.data) return null;
      return (
        <ActionMenu
          items={buildActions(params.data)}
          showIcons
          buttonClassName="hover:bg-neutral-100 dark:hover:bg-neutral-800"
          iconClassName="text-neutral-600 dark:text-neutral-400"
        />
      );
    },
    [buildActions]
  );

  const stateColumns: ColDef<State>[] = useMemo(
    () => [
      {
        headerName: 'State Name',
        field: 'state',
        sortable: false,
        headerClass: 'font-bold',
        minWidth: 220,
        cellClass: 'min-w-0 truncate',
        cellRenderer: NameCellRenderer,
      },
      {
        headerName: 'State Code',
        field: 'stateCode',
        sortable: false,
        headerClass: 'font-bold',
        minWidth: 160,
        cellClass: 'min-w-0 truncate',
      },
      {
        headerName: 'Country Name',
        field: 'country',
        sortable: false,
        headerClass: 'font-bold',
        minWidth: 200,
        cellClass: 'min-w-0 truncate',
      },
      {
        headerName: 'Status',
        field: 'status',
        cellRenderer: (params: any) => <StatusTag value={params.value} />,
        sortable: false,
        headerClass: 'font-bold',
        minWidth: 140,
      },
      {
        headerName: 'Action',
        field: '__actions',
        sortable: false,
        width: 80,
        headerClass: 'justify-end text-right',
        cellClass: 'justify-end text-right',
        cellRenderer: ActionsCellRenderer,
      },
    ],
    [ActionsCellRenderer, NameCellRenderer]
  );

  const handleConfirm = useCallback(async () => {
    if (modalData) {
      try {
        if (modalData.status === 'DELETE') {
          await deleteState(String(modalData.selectedIds[0])).unwrap();
        } else {
          await changeStatus({
            ids: modalData.selectedIds,
            status: modalData.status,
          }).unwrap();
        }

        setRefreshToken((prev) => prev + 1);
      } catch (error: any) {
        // Error is handled globally in baseQuery
      } finally {
        setIsModalOpen(false);
      }
    }
  }, [modalData, changeStatus, deleteState]);

  return (
    <>
      <UniversalModuleView<State>
        moduleKey="states"
        title="State List"
        apiEndpoint={API_CONSTANTS.STATES_ENDPOINT}
        columnsConfig={stateColumns}
        rowKey={(row) => row.stateId}
        tableRowRenderer={undefined}
        selectable
        getRowId={(row) => row.stateId}
        refreshToken={refreshToken}
        primaryAction={{
          label: 'Add State',
          icon: Plus,
          onClick: openCreateState,
        }}
        extraActions={({ reset, selectedIds }) => (
          <>
            <IconButton icon={RefreshCw} title="Refresh" onClick={reset} />
            <IconButton
              icon={Unlock}
              title="Activate"
              onClick={() => handleStatusChange(selectedIds, 'Active')}
              disabled={!selectedIds.length}
            />
            <IconButton
              icon={Lock}
              title="Inactivate"
              onClick={() => handleStatusChange(selectedIds, 'Inactive')}
              disabled={!selectedIds.length}
            />
          </>
        )}
        searchPlaceholder="Search states by name or code..."
      />

      <StateFormModal
        isOpen={isFormOpen}
        mode={formMode}
        stateId={formId}
        onClose={() => {
          setIsFormOpen(false);
          setFormId(undefined);
          if (modalReturnTo) {
            navigate(modalReturnTo);
          }
        }}
        onSuccess={() => setRefreshToken((prev) => prev + 1)}
      />

      <StateDetailsModal
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        stateName={selectedState?.state}
        stateCode={selectedState?.stateCode}
        countryName={selectedState?.country}
        status={selectedState?.status}
        onEdit={() => {
          setIsDetailsOpen(false);
          if (selectedState) openEditState(selectedState.stateId);
        }}
      />

      {isModalOpen && modalData && (
        <BaseModal
          modalData={modalData}
          labels={labels}
          isLoading={isLoading || isDeleteLoading}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};

export default States;
